// Product modules (addendum §1): WhatsApp agent, AI call agent and live call copilot. Each workspace enables
// them independently. Enablement (access) is separate from setup completeness and integration health.
import { z } from 'zod';

export const PRODUCT_MODULES = ['whatsapp', 'ai_calls', 'copilot'] as const;
export const ProductModuleSchema = z.enum(PRODUCT_MODULES);
export type ProductModule = z.infer<typeof ProductModuleSchema>;

/** Default when a workspace has no stored row: the two existing channels on, the copilot off. */
export const MODULE_DEFAULT_ENABLED: Record<ProductModule, boolean> = { whatsapp: true, ai_calls: true, copilot: false };

export const MODULE_LABELS: Record<ProductModule, string> = {
  whatsapp: 'Agente WhatsApp',
  ai_calls: 'Agente de llamadas IA',
  copilot: 'Copiloto de llamadas',
};

export const MODULE_DESCRIPTIONS: Record<ProductModule, string> = {
  whatsapp: 'Mensajes con IA: califica, da seguimiento y agenda citas; tu equipo atiende desde la bandeja.',
  ai_calls: 'Llamadas telefónicas autónomas con el agente de voz para calificar y agendar citas.',
  copilot: 'Asiste a un asesor humano durante su llamada de Aircall con transcripción en vivo, playbooks y conocimiento verificado.',
};

export const ModuleSetupStatusSchema = z.enum(['ready', 'needs_setup', 'unavailable']);
export type ModuleSetupStatus = z.infer<typeof ModuleSetupStatusSchema>;

export const MODULE_SETUP_STATUS_LABELS: Record<ModuleSetupStatus, string> = {
  ready: 'Listo',
  needs_setup: 'Falta configurar',
  unavailable: 'No disponible',
};

export const ModuleHealthSchema = z.object({
  /** Connections of the providers this module uses (WhatsApp: Kapso/HighLevel; calls and copilot: Aircall). */
  connections: z.number().int(),
  connected: z.number().int(),
  needsAttention: z.number().int(),
  /** Human summary, e.g. «1 conexión requiere atención». Null when there is nothing to report. */
  message: z.string().nullable(),
});
export type ModuleHealth = z.infer<typeof ModuleHealthSchema>;

export const ModuleMetricSchema = z.object({
  label: z.string(),
  /** Null = unknown (never shown as 0). */
  value: z.number().int().nullable(),
  /** Path relative to /w/:workspaceId where the metric can be acted on. */
  path: z.string(),
});
export type ModuleMetric = z.infer<typeof ModuleMetricSchema>;

export const WorkspaceModuleStateSchema = z.object({
  module: ProductModuleSchema,
  enabled: z.boolean(),
  /** True when the value comes from a stored decision, false when it is the default. */
  explicit: z.boolean(),
  activatedAt: z.string().nullable(),
  setupStatus: ModuleSetupStatusSchema,
  /** Why the module is not ready (Spanish, actionable). Null when ready. */
  reason: z.string().nullable(),
  health: ModuleHealthSchema,
  metric: ModuleMetricSchema.nullable(),
  /** AI operating window for the channel, when the module has one. */
  aiWindow: z.object({ configured: z.boolean(), openNow: z.boolean(), nextOpenAt: z.string().nullable() }).nullable(),
});
export type WorkspaceModuleState = z.infer<typeof WorkspaceModuleStateSchema>;

export const WorkspaceModulesSchema = z.object({ items: z.array(WorkspaceModuleStateSchema) });
export type WorkspaceModules = z.infer<typeof WorkspaceModulesSchema>;

export const UpdateWorkspaceModuleSchema = z.object({ enabled: z.boolean() });
export type UpdateWorkspaceModule = z.infer<typeof UpdateWorkspaceModuleSchema>;

// ───────── Per-module onboarding (derived from real server state; never a manual tick-box) ─────────

export const ONBOARDING_STEP_STATUSES = ['done', 'pending', 'blocked', 'not_applicable', 'unavailable'] as const;
export const OnboardingStepStatusSchema = z.enum(ONBOARDING_STEP_STATUSES);
export type OnboardingStepStatus = z.infer<typeof OnboardingStepStatusSchema>;

export const ONBOARDING_STEP_STATUS_LABELS: Record<OnboardingStepStatus, string> = {
  done: 'Hecho',
  pending: 'Pendiente',
  blocked: 'Bloqueado',
  not_applicable: 'No aplica',
  unavailable: 'Aún no disponible',
};

/** Who can complete a step: workspace managers, workspace admins, or a platform operator (outside the workspace). */
export const ONBOARDING_ACTORS = ['manager', 'admin', 'platform'] as const;
export type OnboardingActor = (typeof ONBOARDING_ACTORS)[number];
export const ONBOARDING_ACTOR_LABELS: Record<OnboardingActor, string> = {
  manager: 'Gerente o administrador del espacio',
  admin: 'Administrador del espacio',
  platform: 'Operador de la plataforma (Estateflow)',
};

/** Screen that performs a step; the web maps each key to a route. */
export const ONBOARDING_LINK_KEYS = [
  'projects',
  'knowledge',
  'inventory',
  'whatsapp_schedule',
  'voice_schedule',
  'integrations',
  'integrations_highlevel',
  'whatsapp_agent',
  'voice_agent',
  'voice_settings',
  'models',
  'whatsapp_test',
  'voice_test',
  'test_mode',
  'module',
  'copilot_playbooks',
  'copilot_settings',
] as const;
export const OnboardingLinkKeySchema = z.enum(ONBOARDING_LINK_KEYS);
export type OnboardingLinkKey = z.infer<typeof OnboardingLinkKeySchema>;

export interface OnboardingStepDef {
  key: string;
  title: string;
  description: string;
  /** Required steps gate the final activation; optional ones are informative. */
  required: boolean;
  actor: OnboardingActor;
}

const VALIDATION_PENDING = 'Pendiente de validación con una llamada real';

/** Ordered steps per module. Status, reason and link come from the server; titles and explanations live here. */
export const ONBOARDING_STEPS: Record<ProductModule, readonly OnboardingStepDef[]> = {
  whatsapp: [
    { key: 'project', title: 'Crear un proyecto', description: 'Cada conversación se atiende en el contexto de un desarrollo: precios, ubicación y citas dependen del proyecto.', required: true, actor: 'admin' },
    { key: 'knowledge', title: 'Publicar conocimiento', description: 'La IA solo responde con documentos publicados en Conocimiento; sin ellos no puede dar información verificada.', required: true, actor: 'manager' },
    { key: 'inventory', title: 'Inventario y precios', description: 'Unidades con precio y disponibilidad, capturadas aquí o sincronizadas desde el CRM.', required: true, actor: 'admin' },
    { key: 'schedule', title: 'Horario de operación', description: 'Horas en que la IA atiende WhatsApp. Sin horario, la IA no responde.', required: true, actor: 'admin' },
    { key: 'number', title: 'Conectar un número y comprobar el envío', description: 'Un número de WhatsApp conectado por Kapso, o el número conectado en HighLevel (Integraciones → HighLevel → WhatsApp), con una prueba de envío exitosa.', required: true, actor: 'admin' },
    { key: 'automation', title: 'Respuestas automáticas permitidas', description: 'Para números compartidos con la app de WhatsApp Business o con HighLevel, falta ver que una respuesta humana llega a la plataforma, para pausar a la IA.', required: true, actor: 'admin' },
    { key: 'routing', title: 'Asignar el número a un proyecto', description: 'Cada número debe estar enrutado a uno o más proyectos para saber de qué desarrollo se habla.', required: true, actor: 'admin' },
    { key: 'agent', title: 'Publicar la configuración del agente', description: 'Instrucciones, estilo de WhatsApp y criterios de calificación publicados. Un borrador no se usa en conversaciones reales.', required: true, actor: 'admin' },
    { key: 'model', title: 'Modelo de IA disponible', description: 'El modelo que responde por WhatsApp lo asigna la plataforma; el equipo del espacio no puede cambiarlo.', required: true, actor: 'platform' },
    { key: 'calendly', title: 'Conectar la agenda (Calendly o Cal.com) y hacer una reserva de prueba', description: 'Las citas se agendan en la agenda conectada (Calendly o Cal.com). Una reserva real registrada confirma que la conexión funciona.', required: true, actor: 'admin' },
    { key: 'test_agent', title: 'Probar el agente', description: 'Una conversación en «Probar agente» con respuestas de la IA, antes de hablar con prospectos reales.', required: true, actor: 'manager' },
    { key: 'test_mode', title: 'Modo de prueba', description: 'Con el modo de prueba activo solo se envían mensajes a los números de la lista permitida. Un administrador puede desactivarlo en Configuración → Espacio para enviar a prospectos reales.', required: false, actor: 'admin' },
    { key: 'activate', title: 'Activar el módulo', description: 'Con todo lo anterior listo y el módulo activo, la IA empieza a atender WhatsApp.', required: true, actor: 'admin' },
  ],
  ai_calls: [
    { key: 'project', title: 'Crear un proyecto', description: 'Cada llamada se hace en el contexto de un desarrollo.', required: true, actor: 'admin' },
    { key: 'schedule', title: 'Horario de llamadas', description: 'Horas en que la IA puede llamar. Sin horario, la IA no llama.', required: true, actor: 'admin' },
    { key: 'runtime', title: 'Elegir y conectar el motor de voz', description: 'Telefonía conectada y el motor elegido: líneas de Telnyx con el servicio de voz propio, o Aircall (agente nativo de Aircall o servicio de voz propio).', required: true, actor: 'admin' },
    { key: 'lines', title: 'Asignar líneas a un proyecto', description: 'Al menos una línea (Telnyx o Aircall) enrutada a un proyecto y, con el agente nativo de Aircall, con su agente de IA configurado.', required: true, actor: 'admin' },
    { key: 'agent', title: 'Publicar la configuración del agente de voz', description: 'Instrucciones y estilo de voz publicados. Con el agente nativo de Aircall, además, confirmados en Aircall.', required: true, actor: 'admin' },
    { key: 'model', title: 'Modelo de IA disponible', description: 'El modelo de voz lo asigna la plataforma; el equipo del espacio no puede cambiarlo.', required: true, actor: 'platform' },
    { key: 'test_call', title: 'Llamada de prueba registrada', description: 'Una prueba de voz en el navegador o una llamada de prueba real completada y registrada.', required: true, actor: 'admin' },
    { key: 'inbound', title: 'Política de llamadas entrantes', description: 'Si la IA atiende llamadas entrantes en las líneas configuradas (desactivado por defecto).', required: false, actor: 'admin' },
    { key: 'test_mode', title: 'Modo de prueba', description: 'Con el modo de prueba activo solo se llama a los números de la lista permitida. Un administrador puede desactivarlo en Configuración → Espacio para llamar a prospectos reales.', required: false, actor: 'admin' },
    { key: 'activate', title: 'Activar el módulo', description: 'Con todo lo anterior listo y el módulo activo, la IA puede hacer llamadas.', required: true, actor: 'admin' },
  ],
  copilot: [
    { key: 'aircall', title: 'Conectar la telefonía', description: 'El copiloto acompaña llamadas de asesores hechas en Aircall o en líneas de Telnyx.', required: true, actor: 'admin' },
    { key: 'ai_assist_pro', title: 'Licencia Aircall AI Assist Pro', description: `Solo con Aircall: necesaria para recibir su transcripción en vivo (con líneas de Telnyx no hace falta: LIA transcribe con su propio servicio). La plataforma aún no puede comprobarla. ${VALIDATION_PENDING}.`, required: true, actor: 'admin' },
    { key: 'live_transcription', title: 'Transcripción en vivo por número', description: `En Aircall debe activarse para cada número que use el copiloto; en Telnyx la hace el servicio de voz de LIA. ${VALIDATION_PENDING}.`, required: true, actor: 'admin' },
    { key: 'spanish', title: 'Transcripción en español', description: `La calidad de la transcripción en español de México aún no se ha comprobado. ${VALIDATION_PENDING}.`, required: true, actor: 'admin' },
    { key: 'playbooks', title: 'Playbooks', description: 'Guías de llamada que el copiloto sigue paso a paso. Se crean en Copiloto → Playbooks (formulario o texto pegado), se prueban y se publican.', required: true, actor: 'manager' },
    { key: 'live_coaching', title: 'Asistencia en vivo', description: 'Sugerencias durante la llamada con conocimiento verificado, en la pantalla de sesión y en la ventana compacta. Probada solo con un Aircall simulado, un Telnyx simulado y el simulador local: sin verificar con una llamada real.', required: true, actor: 'manager' },
    { key: 'activate', title: 'Activar el módulo', description: 'El copiloto no puede ponerse en marcha hasta completar los pasos anteriores y validarlos con una llamada real.', required: true, actor: 'admin' },
  ],
};

export const COPILOT_VALIDATION_PENDING = VALIDATION_PENDING;

export const OnboardingStepSchema = z.object({
  key: z.string(),
  status: OnboardingStepStatusSchema,
  required: z.boolean(),
  actor: z.enum(ONBOARDING_ACTORS),
  /** Why the step is not done (or why it does not apply), in Spanish. Null when there is nothing to add. */
  reason: z.string().nullable(),
  /** Short fact from the real state when done (e.g. «2 proyectos»). */
  detail: z.string().nullable(),
  linkKey: OnboardingLinkKeySchema.nullable(),
});
export type OnboardingStep = z.infer<typeof OnboardingStepSchema>;

export const ModuleOnboardingSchema = z.object({
  module: ProductModuleSchema,
  enabled: z.boolean(),
  /** All required steps done and the module enabled. */
  ready: z.boolean(),
  completed: z.number().int(),
  requiredTotal: z.number().int(),
  steps: z.array(OnboardingStepSchema),
});
export type ModuleOnboarding = z.infer<typeof ModuleOnboardingSchema>;
